import { m } from '@/i18n';
import {
  compareListSlots,
  decideListPlacement,
  mergeRankPatch,
  normalizeRankMap,
  rankedInPlace,
  type ListSlot,
  type RankMap,
  type RankPatch,
} from './rank-order';
import { errOf, isRecord, ok, type Result } from './types';

/**
 * THE CHECKLIST MODULE (docs/CHECKLIST.md §2, ruled by Leon 2026-11-03): the
 * lines of an item's list live on the item, as data of its `checklist` module,
 * and never in the graph. "Buy carrots" under "Shopping" and "vendor
 * evaluation" under "Lanxess" were the same parent edge for the model and two
 * different forms for a person; nothing derived over the tree told them apart,
 * so the lines of a checklist left the graph.
 *
 * A LINE is text with a box: no seat, no date, no health, no sight row of its
 * own, never a member of a board, never in the rollup or the conclusion
 * question. Lines nest under lines. A line becomes an item only when a person
 * promotes it (`planPromotion`), and a child item that has nothing but a title
 * may be made a line again (`planDemotion`, `isBareChild`).
 *
 * The module's data is `module_data.checklist`:
 *
 * - `lines`: the lines in the order they were written, each
 *   `{ id, text, ticked, parentLineId }`;
 * - `order`: the item's ONE rank map over its whole list, keyed by line id or
 *   by direct child item id (`src/domain/rank-order.ts`), so a parent holds the
 *   order of its lines and its sub-items together, and a child of two parents
 *   has a place in each parent's list.
 *
 * Every act on a line is one event whose `newValue` names the line by id in a
 * field path (`checklist.lines.<id>`, the way `schedule.endTime` is a field
 * path), applied line-wise to the stored data under the item's row lock: two
 * phones ticking different lines never clobber each other, because neither
 * write carries the other's line. Pure: no I/O, no clock.
 */

/** The module's key in `module_data` (`MODULE_KEYS`). */
export const CHECKLIST_KEY = 'checklist' as const;

/** The longest text a line holds. */
export const LINE_TEXT_MAX = 500;
/** The most lines one item's list holds. */
export const LINES_MAX = 500;

export interface ChecklistLine {
  /** `l-` and a key minted where the line was typed (`isLineId`): never a
   *  uuid, so no reader that recognizes item ids in a payload takes it for
   *  one, and a rank map's keys say which entries are lines. */
  id: string;
  text: string;
  ticked: boolean;
  /** The line this one nests under, or null at the top of the list. */
  parentLineId: string | null;
}

export interface Checklist {
  /** In the order they were written: the unranked tail's order. */
  lines: readonly ChecklistLine[];
  /** The item's rank map over its list: line ids and direct child ids. */
  order: RankMap;
}

export const EMPTY_CHECKLIST: Checklist = { lines: [], order: {} };

const LINE_ID = /^l-[A-Za-z0-9_-]{4,64}$/;

/** A line id's shape (`ChecklistLine.id`). */
export function isLineId(id: unknown): id is string {
  return typeof id === 'string' && LINE_ID.test(id);
}

/** A line id from a key minted at the edge (the client's `mintKey`, the
 *  server's random bytes): `l-` and the key's safe characters. */
export function lineIdFrom(key: string): string {
  const safe = key.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 60);
  return `l-${safe.length >= 4 ? safe : safe.padEnd(4, '0')}`;
}

/** The field path an event names a line by. */
export function linePath(lineId: string): string {
  return `${CHECKLIST_KEY}.lines.${lineId}`;
}

/** The field path an event names the list's ranks by: one object keyed by
 *  entry id (a line id or a child item id), so a reader that strips the ids of
 *  items a viewer cannot open from a payload finds each one as a key. */
export const ORDER_PATH = `${CHECKLIST_KEY}.order`;

/** Record one entry's rank (or its removal, null) in an event value. */
export function setRank(
  value: Record<string, unknown>,
  entryId: string,
  rank: string | null,
): void {
  const held = value[ORDER_PATH];
  value[ORDER_PATH] = { ...(isRecord(held) ? held : {}), [entryId]: rank };
}

/** The field path a promotion names the item a line became by. */
export function promotedPath(lineId: string): string {
  return `${CHECKLIST_KEY}.promoted.${lineId}`;
}

/** The field path a demotion names the item a line was made from by. */
export function demotedPath(lineId: string): string {
  return `${CHECKLIST_KEY}.demoted.${lineId}`;
}

/** A path's parts, when it is one of the module's: what it names and by which
 *  id. Null for any other field path. */
export function checklistPathOf(
  path: string,
): { part: 'lines' | 'promoted' | 'demoted'; id: string } | { part: 'order' } | null {
  if (path === ORDER_PATH) return { part: 'order' };
  const match = /^checklist\.(lines|promoted|demoted)\.(.+)$/.exec(path);
  if (!match) return null;
  return { part: match[1] as 'lines' | 'promoted' | 'demoted', id: match[2]! };
}

/** One stored line read back, or null when it does not hold together. */
function lineOf(raw: unknown): ChecklistLine | null {
  if (!isRecord(raw)) return null;
  const { id, text, ticked, parentLineId } = raw;
  if (!isLineId(id) || typeof text !== 'string' || text.trim() === '') return null;
  return {
    id,
    text,
    ticked: ticked === true,
    parentLineId: isLineId(parentLineId) ? parentLineId : null,
  };
}

/**
 * An item's list as stored, read back: only lines that hold together survive,
 * a line whose parent line is gone sits at the top, and a cycle is broken at
 * the first line that closes it. Tolerant by design, like `normalizeRankMap`:
 * a bad stored value never puts a line in an undefined place.
 */
export function checklistOf(moduleData: unknown): Checklist {
  const data = isRecord(moduleData) ? moduleData[CHECKLIST_KEY] : undefined;
  if (!isRecord(data)) return EMPTY_CHECKLIST;
  const seen = new Set<string>();
  const lines: ChecklistLine[] = [];
  for (const raw of Array.isArray(data.lines) ? data.lines : []) {
    const line = lineOf(raw);
    if (!line || seen.has(line.id)) continue;
    seen.add(line.id);
    lines.push(line);
  }
  // Walk up from each line in the stored order; a missing parent or a cycle
  // puts the line at the top, and the walks after it see the fix, so a cycle
  // is broken once, at its first line.
  const parentOf = new Map(lines.map((l) => [l.id, l.parentLineId]));
  for (const l of lines) {
    const path = new Set([l.id]);
    let at = parentOf.get(l.id) ?? null;
    while (at !== null) {
      if (path.has(at) || !parentOf.has(at)) {
        parentOf.set(l.id, null);
        break;
      }
      path.add(at);
      at = parentOf.get(at) ?? null;
    }
  }
  return {
    lines: lines.map((l) => ({ ...l, parentLineId: parentOf.get(l.id) ?? null })),
    order: normalizeRankMap(data.order),
  };
}

/**
 * `moduleData` with the list replaced. An empty list (no lines, no order)
 * removes the module's namespace, so an item that never had a list, and one
 * whose list was emptied, store the same thing.
 */
export function withChecklist(
  moduleData: Record<string, unknown>,
  list: Checklist,
): Record<string, unknown> {
  const rest = { ...moduleData };
  delete rest[CHECKLIST_KEY];
  if (list.lines.length === 0 && Object.keys(list.order).length === 0) return rest;
  return {
    ...rest,
    [CHECKLIST_KEY]: {
      lines: list.lines.map((l) => ({ ...l })),
      order: { ...list.order },
    },
  };
}

/** A list's lines counted, ticked of all: the digest's line half. */
export function lineTallyOf(list: Checklist): { total: number; ticked: number } {
  return { total: list.lines.length, ticked: list.lines.filter((l) => l.ticked).length };
}

/** The ITEM ids a stored list's order names: its rank map's keys that are
 *  no line ids (the item's direct children). */
export function orderItemIds(moduleData: unknown): string[] {
  return Object.keys(checklistOf(moduleData).order).filter((id) => !isLineId(id));
}

/**
 * `moduleData` as a viewer may read it: every rank in the list's order that
 * names an item they cannot open is dropped (`open` says which they can). An
 * id is content for an item one cannot open (docs/MODEL.md "Discoverability
 * and request-access"), and the order map is keyed by child ids, so every
 * read that hands an item's module data to a viewer passes it through here.
 * The lines and the other modules are the item's own and stay.
 */
export function withoutHiddenEntries(
  moduleData: Record<string, unknown>,
  open: (id: string) => boolean,
): Record<string, unknown> {
  const raw = moduleData[CHECKLIST_KEY];
  if (!isRecord(raw) || !isRecord(raw.order)) return moduleData;
  const order: Record<string, unknown> = {};
  let dropped = false;
  for (const [id, rank] of Object.entries(raw.order)) {
    if (isLineId(id) || open(id)) order[id] = rank;
    else dropped = true;
  }
  return dropped ? { ...moduleData, [CHECKLIST_KEY]: { ...raw, order } } : moduleData;
}

/** The line as an event value: what it is, without the id its path carries. */
function lineValue(line: ChecklistLine): Record<string, unknown> {
  return { text: line.text, ticked: line.ticked, parentLineId: line.parentLineId };
}

/** Every line nested under `lineId`, at any depth, in the stored order. */
export function descendantLines(list: Checklist, lineId: string): ChecklistLine[] {
  const under = new Set([lineId]);
  const out: ChecklistLine[] = [];
  // The stored order puts no parent before its children, so walk to a fixpoint.
  let grew = true;
  while (grew) {
    grew = false;
    for (const l of list.lines) {
      if (l.parentLineId !== null && under.has(l.parentLineId) && !under.has(l.id)) {
        under.add(l.id);
        out.push(l);
        grew = true;
      }
    }
  }
  return list.lines.filter((l) => out.includes(l));
}

/** A line's text, trimmed and checked. */
function textOf(raw: string): Result<string> {
  const text = raw.replace(/\s+/g, ' ').trim();
  if (text === '') return errOf('invalid_input', m('itemErrors.line.textNeeded'));
  if (text.length > LINE_TEXT_MAX) {
    return errOf('invalid_input', m('itemErrors.line.tooLong', { max: LINE_TEXT_MAX }));
  }
  return ok(text);
}

/** An act on one line of an item's list. */
export type LineAct =
  | { kind: 'add'; lineId: string; text: string; parentLineId?: string | null }
  | { kind: 'edit'; lineId: string; text: string }
  | { kind: 'tick'; lineId: string; ticked: boolean }
  | { kind: 'nest'; lineId: string; parentLineId: string | null }
  | { kind: 'remove'; lineId: string };

export const LINE_ACT_KINDS = ['add', 'edit', 'tick', 'nest', 'remove'] as const;

/**
 * A line act as it arrives over the wire (a server action's argument, an agent
 * tool's input), checked into its type, or refused as `invalid_input`. The
 * values the decider then judges (the id's shape, the text) stay its own.
 */
export function parseLineAct(raw: unknown): Result<LineAct> {
  const bad = () => errOf('invalid_input', m('itemErrors.line.badAct'));
  if (!isRecord(raw) || typeof raw.lineId !== 'string') return bad();
  const lineId = raw.lineId;
  const parent = raw.parentLineId;
  const parentLineId = parent === undefined || parent === null ? null : parent;
  switch (raw.kind) {
    case 'add':
      if (
        typeof raw.text !== 'string' ||
        (parentLineId !== null && typeof parentLineId !== 'string')
      )
        return bad();
      return ok({ kind: 'add', lineId, text: raw.text, parentLineId });
    case 'edit':
      return typeof raw.text === 'string' ? ok({ kind: 'edit', lineId, text: raw.text }) : bad();
    case 'tick':
      return typeof raw.ticked === 'boolean'
        ? ok({ kind: 'tick', lineId, ticked: raw.ticked })
        : bad();
    case 'nest':
      if (parentLineId !== null && typeof parentLineId !== 'string') return bad();
      return ok({ kind: 'nest', lineId, parentLineId });
    case 'remove':
      return ok({ kind: 'remove', lineId });
    default:
      return bad();
  }
}

/** What a line act decided: the next list, and the one event's two halves,
 *  each keyed by the field paths of the lines and ranks it touched. */
export interface LineDecision {
  next: Checklist;
  previousValue: Record<string, unknown>;
  newValue: Record<string, unknown>;
}

const LINE_MISSING = () => errOf('not_found', m('itemErrors.line.notFound'));

/**
 * THE LINE DECIDER: is this act legal on this list, and what does it change.
 * Who may act is the item's write gate, in the spine; this decides the list.
 *
 * - `add` appends a line (the unranked tail, so it lands at the end of its
 *   level). The id is minted where the line was typed, so a retry that already
 *   landed answers `no_change` instead of writing a second line.
 * - `edit` replaces the text, `tick` sets the box.
 * - `nest` moves a line under another line, or to the top (`null`); it clears
 *   the line's rank, since a rank is a place among one level's entries.
 * - `remove` takes the line and every line nested under it, with their ranks.
 */
export function decideLineAct(list: Checklist, act: LineAct): Result<LineDecision> {
  if (!isLineId(act.lineId)) return errOf('invalid_input', m('itemErrors.line.badId'));
  const byId = new Map(list.lines.map((l) => [l.id, l]));
  const line = byId.get(act.lineId);
  switch (act.kind) {
    case 'add': {
      const text = textOf(act.text);
      if (!text.ok) return text;
      const parentLineId = act.parentLineId ?? null;
      if (line) {
        // The retry of an add that already landed.
        if (line.text === text.value && line.parentLineId === parentLineId) {
          return errOf('no_change', m('itemErrors.line.alreadyAdded'));
        }
        return errOf('invalid_input', m('itemErrors.line.badId'));
      }
      if (parentLineId !== null && !byId.has(parentLineId)) return LINE_MISSING();
      if (list.lines.length >= LINES_MAX) {
        return errOf('invalid_input', m('itemErrors.line.tooMany', { max: LINES_MAX }));
      }
      const added: ChecklistLine = {
        id: act.lineId,
        text: text.value,
        ticked: false,
        parentLineId,
      };
      return ok({
        next: { lines: [...list.lines, added], order: list.order },
        previousValue: { [linePath(added.id)]: null },
        newValue: { [linePath(added.id)]: lineValue(added) },
      });
    }
    case 'edit': {
      if (!line) return LINE_MISSING();
      const text = textOf(act.text);
      if (!text.ok) return text;
      if (text.value === line.text) {
        return errOf('no_change', m('itemErrors.line.textUnchanged'));
      }
      return replaced(list, line, { ...line, text: text.value });
    }
    case 'tick': {
      if (!line) return LINE_MISSING();
      if (line.ticked === act.ticked) {
        return errOf(
          'no_change',
          m(act.ticked ? 'itemErrors.line.ticked' : 'itemErrors.line.open'),
        );
      }
      return replaced(list, line, { ...line, ticked: act.ticked });
    }
    case 'nest': {
      if (!line) return LINE_MISSING();
      const parentLineId = act.parentLineId;
      if (parentLineId === line.parentLineId) {
        return errOf('no_change', m('itemErrors.line.nestUnchanged'));
      }
      if (parentLineId !== null) {
        if (!byId.has(parentLineId)) return LINE_MISSING();
        if (
          parentLineId === line.id ||
          descendantLines(list, line.id).some((d) => d.id === parentLineId)
        ) {
          return errOf('invalid_input', m('itemErrors.line.underItself'));
        }
      }
      const moved = { ...line, parentLineId };
      const rank = list.order[line.id];
      const decision = replaced(list, line, moved);
      if (!decision.ok || rank === undefined) return decision;
      const order = { ...list.order };
      delete order[line.id];
      const previousValue = { ...decision.value.previousValue };
      const newValue = { ...decision.value.newValue };
      setRank(previousValue, line.id, rank);
      setRank(newValue, line.id, null);
      return ok({ next: { lines: decision.value.next.lines, order }, previousValue, newValue });
    }
    case 'remove': {
      if (!line) return LINE_MISSING();
      const gone = [line, ...descendantLines(list, line.id)];
      const ids = new Set(gone.map((l) => l.id));
      const previousValue: Record<string, unknown> = {};
      const newValue: Record<string, unknown> = {};
      for (const l of gone) {
        previousValue[linePath(l.id)] = lineValue(l);
        newValue[linePath(l.id)] = null;
        const rank = list.order[l.id];
        if (rank !== undefined) {
          setRank(previousValue, l.id, rank);
          setRank(newValue, l.id, null);
        }
      }
      const order: RankMap = {};
      for (const [id, rank] of Object.entries(list.order)) if (!ids.has(id)) order[id] = rank;
      return ok({
        next: { lines: list.lines.filter((l) => !ids.has(l.id)), order },
        previousValue,
        newValue,
      });
    }
  }
}

function replaced(list: Checklist, was: ChecklistLine, now: ChecklistLine): Result<LineDecision> {
  return ok({
    next: { lines: list.lines.map((l) => (l.id === was.id ? now : l)), order: list.order },
    previousValue: { [linePath(was.id)]: lineValue(was) },
    newValue: { [linePath(now.id)]: lineValue(now) },
  });
}

// --- The list: lines and direct children in one order -----------------------

/** A direct child as the list sees it: the item, when it was attached under
 *  this parent, and the edge, for the tie. */
export interface ListChild {
  id: string;
  attachedAt: Date;
  edgeId: string;
}

/**
 * The entries of ONE level of an item's list, unsorted: at the top
 * (`parentLineId` null) the top-level lines and the direct children, under a
 * line its nested lines. A child item never sits under a line.
 */
export function listSlots(
  list: Checklist,
  children: readonly ListChild[],
  parentLineId: string | null = null,
): ListSlot[] {
  const slots: ListSlot[] = [];
  if (parentLineId === null) {
    for (const c of children) {
      slots.push({
        id: c.id,
        kind: 'child',
        rank: list.order[c.id] ?? null,
        at: c.attachedAt.getTime(),
        tie: c.edgeId,
      });
    }
  }
  list.lines.forEach((l, index) => {
    if (l.parentLineId !== parentLineId) return;
    slots.push({ id: l.id, kind: 'line', rank: list.order[l.id] ?? null, at: index, tie: l.id });
  });
  return slots;
}

/** One level of an item's list, in THE order (`compareListSlots`). */
export function orderedLevel(
  list: Checklist,
  children: readonly ListChild[],
  parentLineId: string | null = null,
): ListSlot[] {
  return listSlots(list, children, parentLineId).sort(compareListSlots);
}

/** The level an entry sits on: null for a child or a top-level line, the
 *  parent line for a nested line, undefined when the id is on no level. */
function levelOf(
  list: Checklist,
  children: readonly ListChild[],
  entryId: string,
): string | null | undefined {
  if (children.some((c) => c.id === entryId)) return null;
  const line = list.lines.find((l) => l.id === entryId);
  return line ? line.parentLineId : undefined;
}

/**
 * A move within an item's list ("this entry after that one", `null` = first),
 * decided against the whole level the entry sits on: the rank patch that lands
 * it. An entry and its neighbour must share a level; a line never crosses into
 * another line's sub-list by a move (that is `nest`, an act of its own).
 */
export function decideListMove(
  list: Checklist,
  children: readonly ListChild[],
  entryId: string,
  afterId: string | null,
): Result<RankPatch> {
  const level = levelOf(list, children, entryId);
  if (level === undefined) return errOf('invalid_input', m('itemErrors.order.notAChild'));
  return decideListPlacement(listSlots(list, children, level), entryId, afterId);
}

/** The list with a rank patch applied (`mergeRankPatch`). */
export function withRanks(list: Checklist, patch: RankPatch): Checklist {
  return { lines: list.lines, order: mergeRankPatch(list.order, patch) };
}

// --- Promotion and demotion -------------------------------------------------

/** A line that may become an item: a top-level, open line. */
export function promotableLine(list: Checklist, lineId: string): Result<ChecklistLine> {
  const line = list.lines.find((l) => l.id === lineId);
  if (!line) return errOf('not_found', m('itemErrors.line.notFound'));
  if (line.ticked) return errOf('invalid_state', m('itemErrors.line.tickedNotPromoted'));
  if (line.parentLineId !== null) {
    return errOf('invalid_state', m('itemErrors.line.nestedNotPromoted'));
  }
  return ok(line);
}

/** The open top-level lines, in the list's order: what "Promote all open
 *  lines" promotes. */
export function openTopLines(list: Checklist, children: readonly ListChild[]): ChecklistLine[] {
  const byId = new Map(list.lines.map((l) => [l.id, l]));
  return orderedLevel(list, children, null)
    .filter((s) => s.kind === 'line')
    .map((s) => byId.get(s.id)!)
    .filter((l) => !l.ticked);
}

export interface Promotion {
  /** The carrier's list after: the line and its sub-lines gone, the new item
   *  in the line's place. */
  carrier: Checklist;
  /** The new item's own list: the promoted line's sub-lines, the direct ones
   *  at its top, with their ranks. */
  child: Checklist;
  /** The carrier's event halves (the removed lines, the moved rank). */
  previousValue: Record<string, unknown>;
  newValue: Record<string, unknown>;
}

/**
 * A LINE BECOMES A CHILD ITEM, in place (docs/CHECKLIST.md §2): the new item
 * takes the line's rank in the carrier's order (the level is ranked first when
 * anything on it is unranked, so the place is exact), the line leaves, and the
 * lines nested under it become the new item's own list - a child's list lives
 * on the child. Only an open top-level line is promoted (`promotableLine`).
 */
export function planPromotion(
  list: Checklist,
  children: readonly ListChild[],
  lineId: string,
  newItemId: string,
): Result<Promotion> {
  const promotable = promotableLine(list, lineId);
  if (!promotable.ok) return promotable;
  const line = promotable.value;
  const ranked = rankedInPlace(listSlots(list, children, null));
  const order = mergeRankPatch(list.order, ranked);
  const rank = order[line.id]!;
  const sub = descendantLines(list, line.id);
  const gone = new Set([line.id, ...sub.map((l) => l.id)]);

  const carrierOrder: RankMap = {};
  for (const [id, r] of Object.entries(order)) if (!gone.has(id)) carrierOrder[id] = r;
  carrierOrder[newItemId] = rank;
  const childOrder: RankMap = {};
  for (const l of sub) if (order[l.id] !== undefined) childOrder[l.id] = order[l.id]!;

  const previousValue: Record<string, unknown> = {};
  const newValue: Record<string, unknown> = {};
  for (const l of [line, ...sub]) {
    previousValue[linePath(l.id)] = lineValue(l);
    newValue[linePath(l.id)] = null;
  }
  newValue[promotedPath(line.id)] = newItemId;
  for (const [id, r] of Object.entries(ranked)) {
    if (gone.has(id)) continue;
    setRank(previousValue, id, list.order[id] ?? null);
    setRank(newValue, id, r);
  }
  for (const l of [line, ...sub]) {
    const was = list.order[l.id];
    if (was !== undefined) setRank(previousValue, l.id, was);
    if (order[l.id] !== undefined) setRank(newValue, l.id, null);
  }
  setRank(newValue, newItemId, rank);
  setRank(previousValue, newItemId, null);

  return ok({
    carrier: { lines: list.lines.filter((l) => !gone.has(l.id)), order: carrierOrder },
    child: {
      lines: sub.map((l) => (l.parentLineId === line.id ? { ...l, parentLineId: null } : l)),
      order: childOrder,
    },
    previousValue,
    newValue,
  });
}

/** The event value of a list given to a new item: each line by its path. */
export function listPaths(list: Checklist): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const l of list.lines) out[linePath(l.id)] = lineValue(l);
  for (const [id, rank] of Object.entries(list.order)) setRank(out, id, rank);
  return out;
}

/**
 * WHAT MAKES A CHILD "NOTHING BUT A TITLE" (docs/CHECKLIST.md §2): the facts
 * the read and the spine gather for one child, decided here once.
 */
export interface BareFacts {
  description: string | null;
  note: string | null;
  acceptanceCriteria: string | null;
  requesters: readonly string[];
  contributors: readonly string[];
  /** Standing seat offers on it. */
  offers: number;
  moduleData: Record<string, unknown>;
  /** Its own direct children. */
  children: number;
  /** User tags and declared health states. */
  tags: number;
  /** Live attachments. */
  files: number;
  /** Flows it is a member of. */
  flows: number;
  /** Its parents, and every other active or proposed edge it is an end of. */
  parents: number;
  otherEdges: number;
  delivered: boolean;
  active: boolean;
  closed: boolean;
}

/**
 * A child is BARE when it holds nothing but its title: no description, note
 * or acceptance criteria, no seat beyond its one requester and no offer, no
 * module data, no children, no tags, no files, no flow, no edge but its one
 * parent edge, and nothing it has done (delivered, active, closed). Only a
 * bare child is offered "Make this a line": the line keeps only its text, so
 * anything else would be lost. One parent, because the line lands on ONE list
 * and the item leaves every list it is on.
 */
export function isBareChild(f: BareFacts): boolean {
  const empty = (s: string | null) => s === null || s.trim() === '';
  return (
    empty(f.description) &&
    empty(f.note) &&
    empty(f.acceptanceCriteria) &&
    f.requesters.length <= 1 &&
    f.contributors.length === 0 &&
    f.offers === 0 &&
    Object.keys(f.moduleData).length === 0 &&
    f.children === 0 &&
    f.tags === 0 &&
    f.files === 0 &&
    f.flows === 0 &&
    f.parents === 1 &&
    f.otherEdges === 0 &&
    !f.delivered &&
    !f.active &&
    !f.closed
  );
}

export interface Demotion {
  carrier: Checklist;
  previousValue: Record<string, unknown>;
  newValue: Record<string, unknown>;
}

/**
 * A BARE CHILD BECOMES A LINE of the carrier at the same rank (the level is
 * ranked first when anything on it is unranked). The line is open and at the
 * top of the list; the item itself is closed by the spine in the same
 * transaction, and its edge to the carrier removed.
 */
export function planDemotion(
  list: Checklist,
  children: readonly ListChild[],
  childId: string,
  lineId: string,
  title: string,
): Result<Demotion> {
  if (!children.some((c) => c.id === childId)) {
    return errOf('invalid_input', m('itemErrors.order.notAChild'));
  }
  if (!isLineId(lineId) || list.lines.some((l) => l.id === lineId)) {
    return errOf('invalid_input', m('itemErrors.line.badId'));
  }
  const text = textOf(title);
  if (!text.ok) return text;
  if (list.lines.length >= LINES_MAX) {
    return errOf('invalid_input', m('itemErrors.line.tooMany', { max: LINES_MAX }));
  }
  const ranked = rankedInPlace(listSlots(list, children, null));
  const order = mergeRankPatch(list.order, ranked);
  const rank = order[childId]!;
  const line: ChecklistLine = { id: lineId, text: text.value, ticked: false, parentLineId: null };
  const rest = { ...order };
  delete rest[childId];
  const previousValue: Record<string, unknown> = { [linePath(lineId)]: null };
  const newValue: Record<string, unknown> = {
    [linePath(lineId)]: lineValue(line),
    [demotedPath(lineId)]: childId,
  };
  for (const [id, r] of Object.entries(ranked)) {
    if (id === childId) continue;
    setRank(previousValue, id, list.order[id] ?? null);
    setRank(newValue, id, r);
  }
  setRank(previousValue, childId, list.order[childId] ?? null);
  setRank(newValue, childId, null);
  setRank(previousValue, lineId, null);
  setRank(newValue, lineId, rank);
  return ok({
    carrier: { lines: [...list.lines, line], order: { ...rest, [lineId]: rank } },
    previousValue,
    newValue,
  });
}
